import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getPayment } from '@/api/payments';
import { LoadingScreen } from '@/features/check/StatusScreen';
import { PaymentFailureScreen } from '@/features/payment/PaymentFailureScreen';
import { PaymentRunner } from '@/features/payment/PaymentRunner';
import { clearDraft, loadDraft } from '@/payment/draft';
import { useCheckout } from './checkoutContext';
import type { PaymentFailure } from '@/api/types';

/**
 * How long to keep asking while the server says `pending`, and how often.
 *
 * A gateway usually reaches our server before it redirects the guest, but not always — a hosted page can send
 * the browser home while its own callback is still in flight. Thirty seconds is far longer than that race
 * takes and short enough that a guest is not left staring at a spinner over dinner.
 */
const POLL_INTERVAL_MS = 1_500;
const POLL_LIMIT = 20;

const TIMED_OUT: PaymentFailure = { code: 'gatewayUnavailable', retryable: true };
const UNREADABLE: PaymentFailure = { code: 'gatewayUnavailable', retryable: true };

/**
 * Where a hosted payment page sends the browser back to.
 *
 * The page that started the payment is gone; this one is rebuilt from nothing but what survived in storage.
 * It asks the server what happened rather than reading the gateway's response out of the query string, which
 * would put the client back in the business of interpreting a payload it cannot verify.
 *
 * Once it knows where the payment stands it hands over to the same runner that drove the first attempt, so a
 * challenge arriving here is obeyed by exactly the code that obeyed the original instruction.
 */
export function PaymentReturnRoute() {
    const { location, check, accessToken } = useCheckout();
    const navigate = useNavigate();

    const draft = useMemo(() => loadDraft(check.id), [check.id]);
    const paymentId = draft?.paymentId;

    const [polls, setPolls] = useState(0);

    const payment = useQuery({
        queryKey: ['payment', paymentId],
        queryFn: ({ signal }) => getPayment(accessToken, paymentId!, signal),
        enabled: !!paymentId,
        refetchInterval: (query) => {
            if (query.state.data?.status !== 'pending') return false;
            if (polls >= POLL_LIMIT) return false;

            setPolls((count) => count + 1);
            return POLL_INTERVAL_MS;
        },
        retry: 1,
    });

    /*
     * No payment recorded: a typed URL, a stale bookmark, or storage cleared mid-flow. Nothing to resolve and
     * nothing to report, so send them back rather than invent an outcome.
     */
    if (!paymentId) return <Navigate to="/check" replace />;

    const retry = () => {
        // A fresh attempt needs a fresh payment, so the spent one does not follow them back.
        clearDraft();
        navigate('/tip');
    };

    const failed = (failure: PaymentFailure) => (
        <PaymentFailureScreen
            location={location}
            check={check}
            failure={failure}
            onRetry={retry}
            onBackToCheck={() => navigate('/check')}
        />
    );

    if (payment.isError) return failed(UNREADABLE);
    if (payment.isPending) return <LoadingScreen />;

    // Still settling. Keep waiting until the poll budget runs out, then say so rather than spin forever.
    if (payment.data.status === 'pending' && polls < POLL_LIMIT) return <LoadingScreen />;
    if (payment.data.status === 'pending') return failed(TIMED_OUT);

    return (
        <PaymentRunner
            location={location}
            check={check}
            accessToken={accessToken}
            paymentId={paymentId}
            initial={{ kind: 'result', result: payment.data }}
            onRetry={retry}
            onBackToCheck={() => navigate('/check')}
        />
    );
}
